/**
 * MachinesPage.tsx
 * Machines list + add/edit/soft-delete page (Phase 1, Item 5).
 *
 * Phase 5.4 (REQ-04 "nearly one-to-one"):
 *   Computes capable-machine counts per rock TaskType from the already-loaded
 *   machine list and surfaces a soft warning Alert for unexpected multi-machine
 *   mappings.
 *
 * Phase 5.5 (REQ-04 / D1):
 *   Each Task-Types chip now displays the machine time in minutes
 *   (MachineTaskCapability.MachineMinutes) alongside the test name, e.g.
 *   "Direct Shear (Rock) · 165 min". Read-only — minutes are set via the
 *   edit form (5.2).  The two known exceptions (Uniaxial Compressive Strength /
 *   UCS with Modulus) are shown with a softer "known exception" note rather
 *   than the generic warning.
 *
 * Layout:
 *   - Toolbar: title, search box, "Add Machine" button
 *   - Rock capability warning banner (Phase 5.4, soft, dismissable)
 *   - MUI Table: Code / Name / Lab Section / Status / Task Types / Actions
 *   - MachineFormDialog: add or edit
 *   - ConfirmDialog: soft-delete confirmation
 *
 * Data flow:
 *   fetchMachines()            → rows (with TaskTypes joined, no N+1)
 *   fetchTaskTypes() → allTaskTypes for the multi-select
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Science, Search } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import {
  fetchMachines,
  createMachine,
  updateMachine,
  deleteMachine,
  deactivateMachine,
  DeactivateNeedsConfirmError,
  type MachineRow,
  type CreateMachinePayload,
  type UpdateMachinePayload,
} from '../../../services/scheduleMachines';
import {
  buildDeactivateOfferState,
  buildRelocationPromptState,
} from './machineDeactivationPrompts';
import { fetchTaskTypes } from '../../../services/scheduleTaxonomy';
import type { MachineTaskTypeRef } from '../../../services/scheduleMachines';
import { MachineFormDialog } from './MachineFormDialog';
import { MachineDefaultTestsDrawer } from './MachineDefaultTestsDrawer';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { machineStatusLabels } from '../../../constants/statusOptions';
import type { MachineStatus } from '../../../types/schedule';
import {
  computeRockCapabilityWarnings,
  type RockCapabilityWarning,
} from '../../../utils/rockCapabilityWarnings';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Status chip color helper ─────────────────────────────────────────────────

function statusColor(status: MachineStatus, theme: ReturnType<typeof useTheme>) {
  switch (status) {
    case 'Active':       return theme.customColors.statusActive;
    case 'OutOfService': return theme.palette.error.main;
    case 'Calibration':
    case 'Maintenance':  return theme.schedule.maintenance;
    case 'Decommissioned': return theme.schedule.noActivity;
    default:             return theme.schedule.noActivity;
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export const MachinesPage: React.FC = () => {
  const theme = useTheme();
  // Fleet changes (add/delete/deactivate) are admin-only; edits stay technician+
  // (matches machineRoutes guards, RBAC audit D3)
  const { isAdmin, canEditScheduling } = usePermissions();
  const [machines, setMachines] = useState<MachineRow[]>([]);
  const [allTaskTypes, setAllTaskTypes] = useState<MachineTaskTypeRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Phase 5.4: whether the rock-capability warning banner is dismissed. */
  const [warnDismissed, setWarnDismissed] = useState(false);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MachineRow | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  /**
   * Phase 5.7: When DELETE is refused (409 LIVE_BOOKING_GUARD), we surface a
   * second ConfirmDialog that offers "Deactivate" as the safe alternative.
   */
  const [deactivateConfirm, setDeactivateConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  /**
   * REQ-06 (round 2): "Tests & defaults" drawer state. Lets an admin set/clear
   * this machine as the per-test default (TestCatalogItem.DefaultMachineKey).
   */
  const [testsDrawerMachine, setTestsDrawerMachine] = useState<MachineRow | null>(null);
  const [testsDrawerOpen, setTestsDrawerOpen] = useState(false);
  /** Re-point confirmation owned here; the drawer fills the state. */
  const [defaultConfirm, setDefaultConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  /** Toast feedback for default-machine writes. */
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Load machines + task type list
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarnDismissed(false); // reset dismiss on fresh load so new warnings are visible
    try {
      const [rows, taskTypes] = await Promise.all([
        fetchMachines(),
        fetchTaskTypes(),
      ]);
      setMachines(rows);
      setAllTaskTypes(
        taskTypes.map((tt) => ({
          TaskTypeKey: tt.TaskTypeKey,
          Name: tt.Name,
          Category: tt.Category,
          // Run Mode Phase A: drives the per-mode minutes helper text in the
          // form dialog. MachineMinutes is per-pairing (set in the dialog),
          // not a task-type property — null here.
          RunMode: tt.RunMode,
          MachineMinutes: null,
        })),
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load machines';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Local search filter
  const filtered = useMemo(() => {
    if (!search.trim()) return machines;
    const q = search.toLowerCase();
    return machines.filter(
      (m) =>
        m.Code.toLowerCase().includes(q) ||
        m.Name.toLowerCase().includes(q) ||
        (m.LabSection?.toLowerCase().includes(q) ?? false) ||
        (m.MachineGroup?.toLowerCase().includes(q) ?? false),
    );
  }, [machines, search]);

  /**
   * Phase 5.4 (REQ-04 "nearly one-to-one"):
   * Derive rock capability counts from the already-loaded machine list.
   * Recomputes whenever machines changes; returns [] until load completes.
   */
  const rockCapabilityWarnings = useMemo<RockCapabilityWarning[]>(
    () => computeRockCapabilityWarnings(machines),
    [machines],
  );
  const unexpectedWarnings = useMemo(
    () => rockCapabilityWarnings.filter((w) => !w.isKnownException),
    [rockCapabilityWarnings],
  );
  const knownExceptions = useMemo(
    () => rockCapabilityWarnings.filter((w) => w.isKnownException),
    [rockCapabilityWarnings],
  );

  // Open add dialog
  const handleAdd = () => {
    setEditTarget(null);
    setFormOpen(true);
  };

  // Open edit dialog
  const handleEdit = (machine: MachineRow) => {
    setEditTarget(machine);
    setFormOpen(true);
  };

  // REQ-06: open the "Tests & defaults" drawer for a machine.
  const handleOpenTests = (machine: MachineRow) => {
    setTestsDrawerMachine(machine);
    setTestsDrawerOpen(true);
  };

  // Save from dialog
  const handleSave = async (data: CreateMachinePayload | UpdateMachinePayload) => {
    if (editTarget) {
      await updateMachine(editTarget.MachineKey, data as UpdateMachinePayload);
    } else {
      await createMachine(data as CreateMachinePayload);
    }
    await load();
  };

  /**
   * G2-10 (audit #2): second-phase relocation prompt. The deactivate call
   * answered 409 needsConfirm with the machine's future bookings — show the
   * count + labelled rows and offer "keep them & deactivate" (advisory: the
   * bookings survive; the Machine Schedule row is flagged). Dialog copy lives
   * in machineDeactivationPrompts.ts (600-line cap).
   */
  const handleRelocationPrompt = (machine: MachineRow, err: DeactivateNeedsConfirmError) => {
    setDeactivateConfirm(buildRelocationPromptState(machine, err, async () => {
      try {
        await deactivateMachine(machine.MachineKey, 'Decommissioned', true);
        await load();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Deactivate failed';
        setError(msg);
      }
    }));
  };

  /**
   * Phase 5.7: When DELETE returns 409 (LIVE_BOOKING_GUARD), surface a
   * "Deactivate instead" ConfirmDialog offering the safe retirement path.
   * G2-10: the deactivate call is two-phase — with future bookings present it
   * 409s with the booking list, which we surface as a relocation prompt.
   */
  const handleDeactivateOffer = (machine: MachineRow, refusalMessage: string) => {
    setDeactivateConfirm(buildDeactivateOfferState(machine, refusalMessage, async () => {
      try {
        await deactivateMachine(machine.MachineKey, 'Decommissioned');
        await load();
      } catch (e: unknown) {
        if (e instanceof DeactivateNeedsConfirmError) {
          // G2-10: future bookings exist — show the relocation prompt.
          handleRelocationPrompt(machine, e);
          return;
        }
        const msg = e instanceof Error ? e.message : 'Deactivate failed';
        setError(msg);
      }
    }));
  };

  // Trigger delete confirmation
  const handleDeleteClick = (machine: MachineRow) => {
    setConfirm({
      open: true,
      title: 'Delete Machine',
      message: `Remove "${machine.Code} – ${machine.Name}"? If this machine has active bookings, deletion will be refused and deactivation will be offered instead.`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await deleteMachine(machine.MachineKey);
          await load();
        } catch (e: unknown) {
          // Phase 5.7: 409 means "live bookings exist — deactivate instead"
          const axiosErr = e as { response?: { data?: { error?: string; code?: string }; status?: number } };
          if (axiosErr?.response?.status === 409 || axiosErr?.response?.data?.code === 'LIVE_BOOKING_GUARD') {
            const msg = axiosErr.response?.data?.error ?? 'This machine has live bookings.';
            handleDeactivateOffer(machine, msg);
          } else {
            const msg = e instanceof Error ? e.message : 'Delete failed';
            setError(msg);
          }
        }
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 2, gap: 2 }}>
      {/* ── Toolbar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Machines
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rock and soil machines with their task-type capabilities.
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Search by code, name, or section…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Add Machine
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Phase 5.4 — Rock capability soft warning (REQ-04 "nearly one-to-one").
          Shown after data loads; non-blocking; dismissable.
          Generic warning = unexpected multi-machine rock test.
          Known-exception note = UCS / UCS with Modulus (both machines expected). */}
      {!loading && !warnDismissed && rockCapabilityWarnings.length > 0 && (
        <Alert
          severity={unexpectedWarnings.length > 0 ? 'warning' : 'info'}
          onClose={() => setWarnDismissed(true)}
          sx={{ borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Rock test / machine mapping
          </Typography>

          {unexpectedWarnings.length > 0 && (
            <Box sx={{ mb: knownExceptions.length > 0 ? 1 : 0 }}>
              <Typography variant="body2" color="inherit">
                Rock tests are normally 1:1 with a machine. The following rock
                tests map to more than one machine — review whether this is
                intentional:
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, mt: 0.5, pl: 2.5 }}
              >
                {unexpectedWarnings.map((w) => (
                  <Box component="li" key={w.taskTypeKey} sx={{ mb: 0.25 }}>
                    <Typography variant="body2">
                      <strong>{w.taskTypeName}</strong>
                      {' '}
                      <Chip
                        label={`${w.machineCount} machines`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.68rem',
                          bgcolor: theme.schedule.conflictSoft,
                          color: '#fff',
                          ml: 0.5,
                        }}
                      />
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {knownExceptions.length > 0 && (
            <Box>
              <Typography variant="body2" color="inherit">
                Known exception (both ELE and GCTS machines are expected):
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, mt: 0.5, pl: 2.5 }}
              >
                {knownExceptions.map((w) => (
                  <Box component="li" key={w.taskTypeKey} sx={{ mb: 0.25 }}>
                    <Typography variant="body2">
                      <strong>{w.taskTypeName}</strong>
                      {' '}
                      <Chip
                        label={`${w.machineCount} machines`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.68rem',
                          bgcolor: theme.schedule.heatmapLow,
                          color: '#fff',
                          ml: 0.5,
                        }}
                      />
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: theme.table.headerBg } }}>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Lab Section</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Task Types</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'No machines match your search.' : 'No machines found.'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((machine, idx) => (
                  <TableRow
                    key={machine.MachineKey}
                    sx={{
                      bgcolor: idx % 2 === 1 ? theme.table.stripeBg : 'transparent',
                      '&:hover': { bgcolor: theme.table.hoverBg },
                    }}
                  >
                    {/* Code */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                        {machine.Code}
                      </Typography>
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <Typography variant="body2">{machine.Name}</Typography>
                    </TableCell>

                    {/* Lab Section */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {machine.LabSection ?? '—'}
                      </Typography>
                    </TableCell>

                    {/* Machine Group (migration 120 — second grouping level) */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {machine.MachineGroup ?? '—'}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={machineStatusLabels[machine.Status] ?? machine.Status}
                        size="small"
                        sx={{
                          bgcolor: statusColor(machine.Status, theme),
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>

                    {/* Task Types — Phase 5.5: show machine time (minutes) per capability. */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {machine.TaskTypes.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        ) : (
                          machine.TaskTypes.map((tt) => {
                            const minutesSuffix =
                              tt.MachineMinutes != null ? ` · ${tt.MachineMinutes} min` : '';
                            const tooltipText =
                              [tt.Category, tt.MachineMinutes != null ? `${tt.MachineMinutes} min machine time` : null]
                                .filter(Boolean)
                                .join(' · ') || '';
                            return (
                              <Tooltip key={tt.TaskTypeKey} title={tooltipText} placement="top">
                                <Chip
                                  label={`${tt.Name}${minutesSuffix}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.68rem',
                                    height: 20,
                                    borderColor: theme.schedule.machineTime,
                                    color: theme.schedule.machineTime,
                                    maxWidth: 300,
                                  }}
                                />
                              </Tooltip>
                            );
                          })
                        )}
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      <Tooltip title="Tests & defaults">
                        <IconButton size="small" onClick={() => handleOpenTests(machine)}>
                          <Science fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEditScheduling && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(machine)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAdmin && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(machine)}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Dialogs ── */}
      <MachineFormDialog
        open={formOpen}
        machine={editTarget}
        allTaskTypes={allTaskTypes}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <ConfirmDialog
        state={confirm}
        onClose={() => setConfirm((prev) => ({ ...prev, open: false }))}
      />

      {/* Phase 5.7: "Deactivate instead" dialog — shown when DELETE is refused */}
      <ConfirmDialog
        state={deactivateConfirm}
        onClose={() => setDeactivateConfirm((prev) => ({ ...prev, open: false }))}
      />

      {/* REQ-06: per-machine "Tests & defaults" drawer */}
      <MachineDefaultTestsDrawer
        open={testsDrawerOpen}
        machine={testsDrawerMachine}
        machines={machines}
        onClose={() => setTestsDrawerOpen(false)}
        onConfirm={(state) => setDefaultConfirm(state)}
        onToast={(message, severity) => setToast({ message, severity })}
      />

      {/* REQ-06: re-point confirmation (test defaults to a different machine) */}
      <ConfirmDialog
        state={defaultConfirm}
        onClose={() => setDefaultConfirm((prev) => ({ ...prev, open: false }))}
      />

      {/* REQ-06: default-machine write feedback */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.severity === 'error' ? 6000 : 3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast?.severity ?? 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};
